"use client"
import classNames from 'classnames';
import { usePathname, useRouter } from "next/navigation";
import type { Dispatch } from 'react';
import { useEffect, useRef } from 'react';
import LoadingIndicator from 'src/components/loadingindicator/LoadingIndicator';
import type { ISearchAction } from 'src/components/search/ISearchAction';
import SearchSvg from 'src/components/svg/SearchSvg';
import styles from './Search.module.css';
import {locale} from "@locale";

type AppRouter = ReturnType<typeof useRouter>;

//#region [Props]
type SearchProps = {
    showSearchResults: boolean;
    dispatch: Dispatch<ISearchAction>;
    searchExpression: string;
    className?: string;
    placeholderText?: string;
    isSearching?: boolean; // only use this id you with to use the search button as a loading indicator
    disabled?: boolean;
    controlled?: boolean;
    disableEnter?: boolean;
    useIconGradient?: boolean;
};
//#endregion

//#region [Component]
export default function Search({ showSearchResults, dispatch, searchExpression, className, placeholderText, isSearching, disabled = false, controlled = false, disableEnter = false, useIconGradient = false }: SearchProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const router = useRouter();
    const pathname = usePathname();
    const phText = (placeholderText ?? (showSearchResults ? locale.search.prompt : locale.search.action));

    useEffect(() => {
        // instead of a controlled component
        if (!controlled && !searchExpression && inputRef.current) {
            inputRef.current.value = "";
        }

    }, [searchExpression, controlled])

    return <div className={classNames(styles.SSearch, { [styles.KinoDk]: process.env.NEXT_PUBLIC_WEBSITE_BRAND === "kinodk" }, className)}>
        <input
            className={classNames(styles.SSearchInput, 'searchInput')}
            type="text"
            placeholder={phText} name="search"
            onKeyUp={event => checkFullSearch(dispatch, event, router, pathname!, disableEnter)}
            onFocus={() => dispatch({ type: "BEGIN_SEARCH" })}
            autoComplete="off"
            ref={inputRef}
            value={controlled ? searchExpression : undefined}
            onChange={controlled ? (event) => dispatch({ type: "DO_SEARCH", searchExpression: event.currentTarget.value ?? "" }) : undefined}
            aria-label={phText}
        />
        <button className={classNames(styles.SSearchButton, 'searchButton')}
            disabled={disabled}
            aria-label={locale.search.action}
            onClick={event => searchIconClick(dispatch, showSearchResults, router, pathname!, searchExpression)}
        >
            {!isSearching && <SearchSvg className={classNames(styles.SSearchSvg, {
                'no-gradient': !useIconGradient
            })} useCustomGradient={useIconGradient} />}
            {isSearching === true && <LoadingIndicator position='InlineCenter' size='small' />}
        </button>
    </div>;
}
//#endregion


//#region [Other] searchReducer
function checkFullSearch(dispatch: Dispatch<ISearchAction>, event: React.KeyboardEvent<HTMLInputElement>, router: AppRouter, pathname: string, disableEnter: boolean) {
    const key = event.key ?? event.keyCode;
    const val = ((event.target as HTMLInputElement)?.value ?? "");
    if (key === "Escape") {
        dispatch({ type: "CLEAR_SEARCH" });
    } else if (disableEnter === false && key === "Enter" && val?.trim()) {
        goToFullSearch(dispatch, router, pathname, val);
    } else {
        dispatch({ type: "DO_SEARCH", searchExpression: val });
    }
}

function goToFullSearch(dispatch: Dispatch<ISearchAction>, router: AppRouter, pathname: string, searchValue: string) {
    dispatch({ type: "CLEAR_SEARCH" });
    let path = "/sok";
    if (pathname.indexOf("/experimental") !== -1) {
        path = pathname;
    }
    router.push(`${path}/?q=${encodeURIComponent(searchValue.trim())}`);
}

function searchIconClick(dispatch: Dispatch<ISearchAction>, showSearchResults: boolean, router: AppRouter, pathname: string, searchExpression: string) {
    if (showSearchResults) {
        goToFullSearch(dispatch, router, pathname, searchExpression);
    } else {
        dispatch({ type: "BEGIN_SEARCH" });
    }
}
//#endregion